import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { withYear } from "@/lib/year";
import { useAuth } from "@/context/AuthContext";
import LeafCard from "@/components/monev/LeafCard";

const TAHUN_OPTS = [2024, 2025, 2026];

// Halaman tautan publik kartu e-Monev: dapat dibuka siapa pun tanpa login,
// HANYA LIHAT (URL: /monev/:code?tahun=YYYY). Hanya isian final yang tampil.
export default function MonevKartu() {
  const { user } = useAuth();
  const { code } = useParams();
  const [params] = useSearchParams();
  const tahun = Number(params.get("tahun"));
  const year = TAHUN_OPTS.includes(tahun) ? tahun : 2026;

  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(
        await api(`/api/public/monev/kartu/${encodeURIComponent(code)}?year=${year}`, {}, false)
      );
    } catch (e) {
      setNotFound(true);
      toast(formatApiError(e.message));
    }
  }, [code, year]);

  useEffect(() => {
    load();
  }, [load]);

  const backTo = user ? (user.role === "ppid" ? "/admin" : "/dashboard") : "/";

  return (
    <div className="min-h-screen bg-[#F3F7FB]">
      <header className="sticky top-0 z-40 border-b border-[#E3EBF3] bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="Logo" className="h-8 w-8 object-contain" />
            <div className="leading-tight">
              <p className="text-sm font-bold text-[#0B2440]">
                APIK · e-Monev {year}
              </p>
              <p className="text-[10px] font-medium text-slate-500">
                PPID - LLDIKTI Wilayah VII
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              data-testid="monev-kartu-readonly-badge"
              className="hidden rounded-full bg-[#E8A33D]/15 px-2.5 py-1 text-[9px] font-bold tracking-[0.08em] text-[#B97A18] sm:inline-block"
            >
              HANYA LIHAT
            </span>
            <Link
              to={backTo}
              data-testid="monev-kartu-back"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E3EBF3] px-4 py-1.5 text-xs font-semibold text-slate-600 transition-colors duration-300 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
            >
              <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
              {user ? "Kembali ke APIK" : "Beranda PPID"}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8" data-testid="monev-kartu-page">
        {notFound ? (
          <p
            data-testid="monev-kartu-notfound"
            className="rounded-2xl border border-dashed border-[#E3EBF3] bg-white px-6 py-10 text-center text-sm leading-relaxed text-slate-400"
          >
            Kartu isian dengan kode “{code}” tidak ditemukan.
          </p>
        ) : !data ? (
          <p className="text-sm font-semibold text-slate-500">Memuat kartu...</p>
        ) : (
          <>
            <nav
              data-testid="monev-kartu-trail"
              className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold text-slate-400"
            >
              {data.trail.map((t) => (
                <span key={t.code} className="flex items-center gap-2">
                  <span className="max-w-[320px] truncate">
                    {withYear(t.title, year)}
                  </span>
                  <span>/</span>
                </span>
              ))}
              <span className="text-[#1479BF]">{data.item.code}</span>
            </nav>
            <LeafCard
              item={data.item}
              sub={data.sub}
              allRequired={data.allRequired}
              canFill={false}
              isAdmin={false}
              accounts={null}
              userKey=""
              year={year}
              onRefresh={load}
              onRefreshLight={load}
              readOnly
            />
          </>
        )}
      </main>
    </div>
  );
}
